import { datadogRum } from '@datadog/browser-rum';
import axios, {
  AxiosError,
  AxiosHeaders,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  CreateAxiosDefaults,
  InternalAxiosRequestConfig,
} from 'axios';

import { resetAnalytics } from '@consumer-frontend/shared';

import { cookieJar } from './cookieJar';
import {
  apiEndpoints,
  cnuappApiBaseUrl,
  getLoginServerTenantID,
  loginServerBaseUrl,
  orchestratorBaseUrl,
} from './endpoints';

const clientId = import.meta.env.VITE_CLIENT_ID || 'cnufe';
const grantType = 'refresh_token';

export type AxiosRequestConfigT = AxiosRequestConfig & { _retry?: boolean; };

export type InternalAxiosRequestConfigT = InternalAxiosRequestConfig & { _retry?: boolean; };

// API client
export const defaultAxiosConfig: CreateAxiosDefaults = {
  baseURL: cnuappApiBaseUrl,
  headers: new AxiosHeaders({
    Accept: 'application/json, text/html, text/plain, */*',
    Authorization: `Bearer ${cookieJar.getAccessToken()}`,
    ClientID: clientId,
    'Content-Type': 'application/json',
    'X-APP-NAME': 'dashboard',
  }),
  withCredentials: true, // Tells axios to use https and cookies rather than plain http.
};
export const apiClient = axios.create(defaultAxiosConfig);

// Login server client
// Can't use the default apiClient here because login server needs `withCredentials` to be false.
export const defaultLoginServerPostData = {
  grant_type: grantType,
};
export const loginServerAxiosConfig: CreateAxiosDefaults = {
  baseURL: loginServerBaseUrl,
  headers: new AxiosHeaders({
    'Content-Type': 'application/json',
  }),
  withCredentials: false,
};
export const loginServerClient = axios.create(loginServerAxiosConfig);

loginServerClient.interceptors.request.use((req) => {
  const tenantId = getLoginServerTenantID();

  if (!tenantId) {
    return req;
  }

  if (!(req.headers instanceof AxiosHeaders)) {
    req.headers = new AxiosHeaders(req.headers);
  }

  req.headers.set('X-Login-Server-Tenant-ID', tenantId);

  return req;
});

// Orchestrator client
// Can't use the default apiClient here because the orchestrator service doesn't support
// credentialed CORS requests (it isn't part of the cnuapp session/cookie auth flow). It still
// requires the same V4 JWT bearer token as cnuapp, plus a recognized `ClientID`.
export const orchestratorAxiosConfig: CreateAxiosDefaults = {
  baseURL: orchestratorBaseUrl,
  headers: new AxiosHeaders({
    Accept: 'application/json',
    ClientID: clientId,
    'Content-Type': 'application/json',
  }),
};
export const orchestratorClient = axios.create(orchestratorAxiosConfig);

// Set fresh on every request rather than baked into the default headers above, since this
// client (unlike apiClient) has no static Authorization default to fall back on between
// refreshes — see the shared ensureFreshToken/refreshAndRetry wiring at the bottom of this file
// for what keeps the underlying cookie value itself current.
orchestratorClient.interceptors.request.use((req) => {
  if (!(req.headers instanceof AxiosHeaders)) {
    req.headers = new AxiosHeaders(req.headers);
  }

  req.headers.set('Authorization', `Bearer ${cookieJar.getAccessToken()}`);

  return req;
});

// Blob client
export const blobFetcher = (url: string) => apiClient.get(url, { responseType: 'blob' }).then((res) => res.data);

// Interceptor helpers
export const noopSuccessHandler = <T = AxiosResponse>(resp: T) => Promise.resolve(resp);
export const noopErrorHandler = (err: AxiosError) => Promise.reject(err);

export const UNAUTHORIZED_STATUS = 401;

export const isUnauthorizedError = (error: unknown): boolean => (
  axios.isAxiosError(error) && error.response?.status === UNAUTHORIZED_STATUS
);

export type LogoutReasonT =
  | 'cnuapp_unauthorized'
  | 'no_refresh_token'
  | 'no_session'
  | 'refresh_failed'
  | 'sign_unauthorized'
  | 'unauthorized_after_refresh';

// One Datadog RUM action per forced logout. Exported so the contract flow's session-expired
// redirect (processOrchestratorResponse), which never reaches logOutAndRedirect, reports the same way.
export const trackLoggedOut = (reason: LogoutReasonT) => {
  datadogRum.addAction('authLoggedOut', { reason });
};

// Export as an object so it can be mocked in tests
export const authHelpers = {
  logOutAndRedirect: (reason: LogoutReasonT) => {
    trackLoggedOut(reason);

    cookieJar.expire();
    resetAnalytics();
    // Some consumers of this client (e.g. apps/contract) persist flow progress — flowId, the
    // furthest step reached, Funds inputs — to sessionStorage so it survives a page refresh (see
    // leaveContractApp/clearFlowStorage in @consumer-frontend/components). This is a logout, not
    // a mere navigation: the session this client was authenticated under is ending, so anything
    // staged for that session should not outlive it. This lib has no reference to those atoms
    // (nor should it — the dependency runs the other way), but sessionStorage itself is a plain
    // browser API, and nothing else in this codebase uses it, so clearing it here is safe and
    // catches every consumer, present or future.
    sessionStorage.clear();
    window.location.href = `${import.meta.env.VITE_CNUFE_HOME}/login`;
  },
};

// Re-export for backward compatibility
export const { logOutAndRedirect } = authHelpers;

export const refreshTokens = (): Promise<AxiosResponse | AxiosError | Error> => {
  const currentToken = cookieJar.getRefreshToken();

  if (!currentToken && import.meta.env.VITE_MOCKING !== 'true') {
    authHelpers.logOutAndRedirect('no_refresh_token');

    return Promise.reject(new Error('No refresh token found'));
  }

  return loginServerClient
    .post(apiEndpoints.token, {
      ...defaultLoginServerPostData,
      refresh_token: currentToken,
    })
    .then(
      (resp: AxiosResponse<{ access_token: string; refresh_token: string; }>) => {
        cookieJar.expire();
        cookieJar.saveTokens(resp.data.access_token, resp.data.refresh_token);

        return Promise.resolve(resp);
      },
      (err: AxiosError) => {
        console.warn('Error refreshing token:', err);
        datadogRum.addAction('authTokenRefreshFailed', {
          status: err.response?.status ?? null,
        });

        return Promise.reject(err);
      },
    )
  ;
};

// Parameterized by client (rather than hardcoded to apiClient) so any client sharing this
// cookie-based session — currently apiClient and orchestratorClient — can wire up the same
// refresh-and-retry behavior instead of each needing its own copy.
export const retryRequest = (client: AxiosInstance) => (
  originalRequest: InternalAxiosRequestConfigT,
): Promise<AxiosResponse | AxiosError> => {
  const newRequest = { ...originalRequest, _retry: true };

  return client.request(newRequest).then(
    (resp: AxiosResponse) => Promise.resolve(resp),
    (err: AxiosError) => Promise.reject(err),
  );
};

export const refreshAndRetry = (client: AxiosInstance) => (
  err: AxiosError,
): Promise<AxiosResponse | AxiosError> => {
  const originalRequest = err.config as InternalAxiosRequestConfigT;
  const status = err.status || err.response?.status;

  if (status && [401, 403].includes(status)) {
    // Retry if the request hasn't already been retried.
    if (!originalRequest._retry) {
      return refreshTokens().then(
        () => retryRequest(client)(originalRequest),
        (refreshError: AxiosError) => {
          authHelpers.logOutAndRedirect('refresh_failed');

          return Promise.reject(refreshError);
        },
      );
    }

    // If the request has already been retried but we're still seeing 401/403 from the API, this means the token refresh
    // succeeded but the session is expired, so log out and redirect.
    authHelpers.logOutAndRedirect('unauthorized_after_refresh');

    return Promise.reject(err);
  }

  return Promise.reject(err);
};

export const ensureFreshToken = (client: AxiosInstance) => (
  req: InternalAxiosRequestConfigT,
): Promise<InternalAxiosRequestConfigT> => {
  if (!cookieJar.isTokenExpired()) {
    return Promise.resolve(req);
  }

  return refreshTokens().then(
    () => {
      const { defaults } = client;

      defaults.headers.Authorization = `Bearer ${cookieJar.getAccessToken()}`;
      req.headers = req.headers || {};
      req.headers.Authorization = `Bearer ${cookieJar.getAccessToken()}`;

      return Promise.resolve(req);
    },
    (err: AxiosError) => {
      authHelpers.logOutAndRedirect('refresh_failed');

      return Promise.reject(err);
    },
  );
};

// Axios interceptors
apiClient.interceptors.request.use(
  ensureFreshToken(apiClient),
  noopErrorHandler,
);

apiClient.interceptors.response.use(
  noopSuccessHandler,
  refreshAndRetry(apiClient),
);

// The orchestrator service doesn't have its own login/session concept — it rides on the same
// cnuapp session cookie — so it gets the same proactive-refresh/401-retry behavior as apiClient.
orchestratorClient.interceptors.request.use(
  ensureFreshToken(orchestratorClient),
  noopErrorHandler,
);

orchestratorClient.interceptors.response.use(
  noopSuccessHandler,
  refreshAndRetry(orchestratorClient),
);
